import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ErrorService } from '../../shared/services/error.service';
import { User } from '../../models/User';
import { UsersService } from '../../services/users.service';
import { StaffService } from '../../services/staff.service';
import { ProfilesService } from '../../services/profiles.service';
import { Profile } from '../../models/Profile';
import { TableAction, TableColumn } from '../../shared/components/table/table.component';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent implements OnInit {

  users: User[] = [];
  filteredUsers: User[] = [];
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  hasNextPage = false;
  hasPreviousPage = false;
  loading = false;
  error: string | null = null;

  // Filtros
  filters = {
    profileId: '',
    active: '',
    employeeType: '',
    isClient: ''
  };

  // Opções para os filtros
  profiles: { id: number, label: string }[] = [];
  profilesMap: Map<number, string> = new Map(); // Mapa para busca rápida de perfis

  columns: TableColumn[] = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Email', label: 'email', formatter: (value: any) => this.formatEmail(value) },
    { titulo: 'Funcionário', label: 'staffDisplay' },
    { titulo: 'Perfil', label: 'profileDisplay' },
    { titulo: 'Status', label: 'statusDisplay' },
    { titulo: 'Criado em', label: 'createdAtDisplay' }
  ];

  // Ações da tabela
  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private fb: FormBuilder,
    private errorService: ErrorService,
    private usersService: UsersService,
    private staffService: StaffService,
    private profilesService: ProfilesService,
    private router: Router
  ) {
   
  }

  selectedUser: any | null = null;
  showUserModal = false;

  ngOnInit(): void {
    this.loading = true;
    this.profilesService.getAllProfiles().pipe(
      catchError((error) => {
        console.error('Erro ao carregar perfis:', error);
        return of([] as Profile[]);
      }),
      switchMap((profiles: Profile[]) => {
        this.profiles = profiles.map(profile => ({ id: profile.id, label: profile.name }));
        profiles.forEach(profile => this.profilesMap.set(profile.id, profile.name));
        return of(null);
      })
    ).subscribe(() => this.loadUsers());
  }

  loadUsers(): void {
    this.loadUsersPaginated(this.currentPage, this.pageSize);
  }

  /**
   * Carrega usuários com paginação
   */
  loadUsersPaginated(page: number = 1, pageSize: number = 10): void {
    this.loading = true;
    this.error = null;

    this.usersService.getUsersPaginated(page, pageSize, {
      active: this.filters.active,
      employeeType: this.filters.employeeType,
      isClient: this.filters.isClient,
      profileId: this.filters.profileId
    }).subscribe({
      next: (response) => {
        this.currentPage = response.pagination.currentPage;
        this.pageSize = response.pagination.pageSize;
        this.totalItems = response.pagination.totalCount;
        this.hasNextPage = this.currentPage < response.pagination.totalPages;
        this.hasPreviousPage = this.currentPage > 1;

        this.loadStaffDataForUsers(response.data);
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Erro ao carregar usuários';
        console.error('Erro ao carregar usuários:', error);
      }
    });
  }

  /**
   * Aplica os filtros selecionados — recarrega da página 1 com os filtros no backend
   */
  applyFilters(): void {
    this.currentPage = 1;
    this.loadUsersPaginated(1, this.pageSize);
  }

  /**
   * Limpa todos os filtros e recarrega
   */
  clearFilters(): void {
    this.filters = {
      profileId: '',
      active: '',
      employeeType: '',
      isClient: ''
    };
    this.currentPage = 1;
    this.loadUsersPaginated(1, this.pageSize);
  }

  private loadStaffDataForUsers(users: User[]): void {
    const staffRequests = users.map(user => {
      if (user.staffId) {
        return this.staffService.getStaffById(user.staffId).pipe(
          catchError((err) => {
            console.error(`Erro ao buscar staff ${user.staffId}:`, err);
            return of(null);
          })
        );
      }
      return of(null);
    });

    forkJoin(staffRequests).subscribe({
      next: (staffMembers) => {
        this.users = this.transformUsersForDisplay(users, staffMembers);
        this.filteredUsers = [...this.users];
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dados de funcionários:', error);
        this.users = this.transformUsersForDisplay(users, []);
        this.filteredUsers = [...this.users];
        this.loading = false;
      }
    });
  }

  private transformUsersForDisplay(users: User[], staffMembers?: any[]): any[] {
    return users.map((user, index) => {
      const staff = staffMembers && staffMembers[index];
      
      // Debug: Log completo do usuário
      
      // Obter profileIds do usuário (pode ser array ou número)
      let profileIds: number[] = [];
      
      // Primeiro, verificar se tem profileIds como array
      if ((user as any).profileIds && Array.isArray((user as any).profileIds)) {
        profileIds = (user as any).profileIds;
      } 
      // Se não tem array, verificar se tem profileId único
      else if (user.profileId) {
        profileIds = [user.profileId];
      }
      // Se não tem nenhum, deixar vazio
      else {
      }
      
      const profileDisplay = this.getProfileLabel(profileIds);
      
      return {
        ...user,
        staffDisplay: staff?.FullName || staff?.name || '-',
        profileDisplay: profileDisplay,
        statusDisplay: this.getStatusLabel(user.active),
        blockedDisplay: this.getBlockedLabel(user.blocked),
        clientDisplay: this.getClientLabel(user.isClient),
        createdAtDisplay: this.formatDate(user.createdAt)
      };
    });
  }

  onSearchChange(searchTerm: string): void {
    // O componente table já gerencia a busca internamente
    // Este método pode ser usado para ações adicionais se necessário
  }

  onPageChange(page: number): void {
    this.loadUsersPaginated(page, this.pageSize);
  }

  onPageSizeChange(size: number): void {
    this.loadUsersPaginated(1, size);
  }

  // Navegação
  navigateToNewUser(): void {
    this.router.navigate(['/users/new']);
  }

  // Métodos para as ações da tabela
  editUser(user: any): void {
    // Navegar para tela de edição usando o ID do usuário
    this.router.navigate(['/users', user.id, 'edit']);
  }

  openViewModal(user: any): void {
    this.selectedUser = user;
    this.showUserModal = true;
  }

  closeViewModal(): void {
    this.showUserModal = false;
    this.selectedUser = null;
  }

  viewUser(user: User): void {
    // Navegar para tela de visualização
    this.router.navigate(['/users', user.id, 'view']);
  }

  deleteUser(user: any): void {
    const userName = user.name || user.email || `ID ${user.id}`;
    if (confirm(`Tem certeza que deseja excluir o usuário "${userName}"?\n\nEsta ação não pode ser desfeita.`)) {
      // Obter o email do usuário atual
      const lastLoggedEmail = localStorage.getItem('lastLoggedEmail');
      let currentUserEmail = '';
      
      if (lastLoggedEmail) {
        currentUserEmail = lastLoggedEmail;
      } else {
        const currentUserJson = localStorage.getItem('currentUser');
        if (currentUserJson) {
          try {
            const currentUser = JSON.parse(currentUserJson);
            currentUserEmail = currentUser.email || '';
          } catch (error) {
            console.error('Erro ao obter usuário:', error);
          }
        }
      }
      
      if (!currentUserEmail) {
        alert('Erro: Não foi possível identificar o usuário atual. Por favor, faça login novamente.');
        return;
      }
      
      this.loading = true;
      this.usersService.delete(user.id, currentUserEmail).subscribe({
        next: () => {
          alert('Usuário excluído com sucesso!');
          this.loadUsers(); // Recarregar a lista
        },
        error: (error) => {
          this.loading = false;
          this.error = 'Erro ao excluir usuário';
          console.error('Erro ao excluir usuário:', error);
          alert('Erro ao excluir usuário. Verifique o console para mais detalhes.');
        }
      });
    }
  }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;
    switch (action) {
      case 'view':
        this.openViewModal(item);
        break;
      case 'edit':
        this.editUser(item);
        break;
      case 'delete':
        this.deleteUser(item);
        break;
      default:
    }
  }

  refreshUsers(): void {
    this.loadUsers();
  }



  // Métodos auxiliares para formatação na tabela
  getProfileLabel(profileIds: number | number[]): string {
    
    // Se for um array de IDs
    if (Array.isArray(profileIds)) {
      if (profileIds.length === 0) return 'Sem perfil';
      const profileNames = profileIds
        .map(id => {
          const name = this.profilesMap.get(id);
          return name || `Perfil ${id}`;
        })
        .join(', ');
      return profileNames;
    }
    // Se for um ID único (compatibilidade com versão antiga)
    if (profileIds) {
      const name = this.profilesMap.get(profileIds);
      return name || `Perfil ${profileIds}`;
    }
    return 'Sem perfil';
  }

  getStatusLabel(active: boolean): string {
    return active ? 'Ativo' : 'Inativo';
  }

  getBlockedLabel(blocked: boolean): string {
    return blocked ? 'Sim' : 'Não';
  }

  getClientLabel(isClient: boolean): string {
    return isClient ? 'Cliente' : 'Funcionário';
  }

  formatEmail(value: any): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const email = value.toString();

    if (email.length <= 14) {
      return email;
    }

    return email.substring(0, 14) + '...';
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR');
  }

  // ===== Métodos de Validação =====

  /**
   * Valida senha forte
   */
  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    return this.usersService.validatePassword(password);
  }

  /**
   * Valida formato de email
   */
  validateEmail(email: string): boolean {
    return this.usersService.validateEmail(email);
  }

  // ===== Métodos de Busca Avançada =====

  /**
   * Busca usuários ativos
   */
  loadActiveUsers(): void {
    this.loading = true;
    this.error = null;

    this.usersService.getActive().subscribe({
      next: (users: User[]) => {
        this.loadStaffDataForUsers(users);
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Erro ao carregar usuários ativos';
        console.error('Erro ao carregar usuários ativos:', error);
      }
    });
  }

  /**
   * Busca usuário por email
   */
  searchUserByEmail(email: string): void {
    if (!email.trim()) {
      this.loadUsers();
      return;
    }

    this.loading = true;
    this.error = null;

    this.usersService.getByEmail(email).subscribe({
      next: (user: User | null) => {
        if (user) {
          this.loadStaffDataForUsers([user]);
        } else {
          this.users = [];
          this.loading = false;
        }
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Erro ao buscar usuário por email';
        console.error('Erro ao buscar usuário por email:', error);
      }
    });
  }

  /**
   * Busca usuários por tipo de vínculo
   */
  loadUsersByEmployeeType(employeeType: 'CLT' | 'PJ'): void {
    this.loading = true;
    this.error = null;

    this.usersService.getByTipoVinculo(employeeType).subscribe({
      next: (users: User[]) => {
        this.loadStaffDataForUsers(users);
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Erro ao carregar usuários por tipo de vínculo';
        console.error('Erro ao carregar usuários por tipo de vínculo:', error);
      }
    });
  }

  // ===== Métodos de Gerenciamento de Senha =====

  /**
   * Gera senha padrão para usuário
   */
  generateDefaultPassword(userId: number): void {
    this.usersService.generateDefaultPassword(userId).subscribe({
      next: (response) => {
        // Aqui você pode mostrar uma notificação de sucesso
      },
      error: (error) => {
        console.error('Erro ao gerar senha padrão:', error);
        // O ErrorService já mostra o erro visual
      }
    });
  }

  /**
   * Solicita reset de senha
   */
  requestPasswordReset(email: string): void {
    this.usersService.requestPasswordReset(email).subscribe({
      next: (response) => {
        // Aqui você pode mostrar uma notificação de sucesso
      },
      error: (error) => {
        console.error('Erro ao solicitar reset de senha:', error);
        // O ErrorService já mostra o erro visual
      }
    });
  }

  // ===== Métodos de Verificação de Disponibilidade =====

  /**
   * Verifica se email está disponível
   */
  checkEmailAvailability(email: string, excludeUserId?: number): void {
    this.usersService.checkEmailAvailability(email, excludeUserId).subscribe({
      next: (response) => {
        // Aqui você pode usar a resposta para validação em formulários
      },
      error: (error) => {
        console.error('Erro ao verificar disponibilidade do email:', error);
      }
    });
  }
}

